import React, { useEffect, useState } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

export default function UserManagement() {
  const { getModuleLabel, getMessage, translate } = useTranslation();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'general_user',
    status: 'active',
    verified: false
  });

  const userStatuses = [
    { value: 'active', label: `🟢 ${getMessage('user_statuses.active')}`, color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: `⚪ ${getMessage('user_statuses.inactive')}`, color: 'bg-gray-100 text-gray-800' },
    { value: 'suspended', label: `🟡 ${getMessage('user_statuses.suspended')}`, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'cancelled', label: `🔴 ${getMessage('user_statuses.cancelled')}`, color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await API.get('/admin/users');
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error(`❌ ${getMessage('error_messages.load_users_failed')}`);
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await API.get('/roles');
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      // Set default roles as fallback
      setRoles([
        { id: 1, name: 'admin', permission_count: 0 },
        { id: 2, name: 'general_user', permission_count: 0 }
      ]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      role: 'general_user',
      status: 'active',
      verified: false
    });
    setEditingUser(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error(`❌ ${getMessage('form_validation.name_required')}`);
      return false;
    }
    if (!formData.email.trim()) {
      toast.error(`❌ ${getMessage('form_validation.email_required')}`);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error(`❌ ${getMessage('form_validation.invalid_email')}`);
      return false;
    }
    if (!editingUser && !formData.password.trim()) {
      toast.error(`❌ ${getMessage('form_validation.password_required')}`);
      return false;
    }
    if (!editingUser && formData.password !== formData.confirmPassword) {
      toast.error(`❌ ${getMessage('form_validation.passwords_not_match')}`);
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error(`❌ ${getMessage('form_validation.password_min_length')}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const userData = { ...formData };
      delete userData.confirmPassword;
      
      if (editingUser) {
        // Update user
        const response = await API.put(`/admin/users/${editingUser.id}`, userData);
        setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
        toast.success(`✅ ${getMessage('success_messages.user_updated')}`);
      } else {
        // Create new user
        const response = await API.post('/admin/users', userData);
        // Refresh users list to get complete data with permissions
        await fetchUsers();
        toast.success(`✅ ${getMessage('success_messages.user_added')}`);
      }
      
      resetForm();
      setShowAddForm(false);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      const action = editingUser ? getMessage('users.update_user') : getMessage('users.add_user');
      toast.error(`❌ ${translate('error_messages.update_user_failed', { action, message })}`);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      confirmPassword: '',
      phone: user.phone || '',
      role: user.role,
      status: user.status,
      verified: user.verified
    });
    setShowAddForm(true);
  };

  const deleteUser = async (id, userName) => {
    const confirmMessage = translate('confirmation_messages.delete_user_confirm', { userName });
    if (window.confirm(confirmMessage)) {
      try {
        await API.delete(`/admin/users/${id}`);
        setUsers(users.filter(u => u.id !== id));
        toast.success(`✅ ${translate('success_messages.user_deleted', { userName })}`);
      } catch (error) {
        toast.error(`❌ ${getMessage('error_messages.delete_user_failed')}`);
      }
    }
  };

  const toggleUserStatus = async (id, currentStatus, userName) => {
    const statusOptions = [
      { value: 'active', label: getMessage('user_statuses.active') },
      { value: 'inactive', label: getMessage('user_statuses.inactive') },
      { value: 'suspended', label: getMessage('user_statuses.suspended') },
      { value: 'cancelled', label: getMessage('user_statuses.cancelled') }
    ];

    const currentIndex = statusOptions.findIndex(s => s.value === currentStatus);
    const nextIndex = (currentIndex + 1) % statusOptions.length;
    const newStatus = statusOptions[nextIndex].value;
    const newStatusLabel = statusOptions[nextIndex].label;

    const confirmMessage = translate('confirmation_messages.change_status_confirm', { userName, newStatus: newStatusLabel });
    if (window.confirm(confirmMessage)) {
      try {
        await API.put(`/admin/users/${id}/status`, { status: newStatus });
        setUsers(users.map(u => u.id === id ? { ...u, status: newStatus } : u));
        toast.success(`✅ ${translate('success_messages.status_changed', { newStatus: newStatusLabel })}`);
      } catch (error) {
        toast.error(`❌ ${getMessage('error_messages.change_status_failed')}`);
      }
    }
  };

  const toggleVerification = async (id, currentStatus, userName) => {
    const newStatus = !currentStatus;
    const action = newStatus ? getMessage('confirmation_messages.verify') : getMessage('confirmation_messages.unverify');
    
    const confirmMessage = translate('confirmation_messages.verify_user_confirm', { action, userName });
    if (window.confirm(confirmMessage)) {
      try {
        await API.put(`/admin/users/${id}/verify`, { verified: newStatus });
        setUsers(users.map(u => u.id === id ? { ...u, verified: newStatus } : u));
        toast.success(`✅ ${translate('success_messages.verification_changed', { action })}`);
      } catch (error) {
        toast.error(`❌ ${getMessage('error_messages.change_verification_failed')}`);
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          👥 {getModuleLabel('users', 'title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {getModuleLabel('users', 'subtitle')}
        </p>
      </div>

      {/* Header Actions */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(!showAddForm);
            }}
            className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            ➕ {getModuleLabel('users', 'add_user')}
          </button>
          <button
            onClick={fetchUsers}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            🔄 {getModuleLabel('users', 'refresh')}
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            🔍 {getModuleLabel('users', 'search_users')}
          </label>
          <input
            type="text"
            placeholder={getModuleLabel('users', 'search_placeholder')}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            🔑 {getModuleLabel('users', 'filter_by_role')}
          </label>
          <select
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="all">{getModuleLabel('users', 'all_roles')}</option>
            {roles.map(role => (
              <option key={role.id} value={role.name}>
                {role.name === 'admin' ? '🔑 Admin' : role.name === 'general_user' ? '👤 General User' : role.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            🏷️ {getModuleLabel('users', 'filter_by_status')}
          </label>
          <select
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{getModuleLabel('users', 'all_statuses')}</option>
            {userStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ✅ {getModuleLabel('users', 'verification')}
          </label>
          <select
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={filteredUsers.filter(u => u.verified).length > 0 ? 'verified' : 'all'}
            onChange={(e) => {
              // This is just for display, actual filtering handled in filteredUsers
            }}
          >
            <option value="all">{getModuleLabel('users', 'all_verification')}</option>
            <option value="verified">{getModuleLabel('users', 'verified_only')}</option>
            <option value="unverified">{getModuleLabel('users', 'unverified_only')}</option>
          </select>
        </div>
      </div>

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div className="mb-6 p-6 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">
            {editingUser ? `✏️ ${getModuleLabel('users', 'edit_existing_user')}` : `➕ ${getModuleLabel('users', 'add_new_user')}`}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {getModuleLabel('users', 'full_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder={getModuleLabel('users', 'full_name')}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {getModuleLabel('users', 'email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="user@example.com"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {getModuleLabel('users', 'phone')}
                </label>
                <input
                  type="tel"
                  placeholder="02-xxx-xxxx"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {getModuleLabel('users', 'role')} <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>
                      {role.name === 'admin' ? '🔑 Admin' : role.name === 'general_user' ? '👤 General User' : role.name}
                      {role.permission_count > 0 && ` (${role.permission_count} permissions)`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {getModuleLabel('users', 'status')} <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  {userStatuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="verified"
                  className="mr-2"
                  checked={formData.verified}
                  onChange={(e) => setFormData({...formData, verified: e.target.checked})}
                />
                <label htmlFor="verified" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  ✅ {getModuleLabel('users', 'verified_identity')}
                </label>
              </div>
            </div>

            {!editingUser && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {getModuleLabel('users', 'password')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    placeholder={getModuleLabel('users', 'min_password_length')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {getModuleLabel('users', 'confirm_password')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    placeholder={getModuleLabel('users', 'confirm_password_again')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  />
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded transition-colors duration-200"
              >
                {editingUser ? `✅ ${getModuleLabel('users', 'update_user')}` : `✅ ${getModuleLabel('users', 'add_user')}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowAddForm(false);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors duration-200"
              >
                ❌ {getModuleLabel('users', 'cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
            {users.length}
          </div>
          <div className="text-blue-600 dark:text-blue-400 text-sm">👥 {getModuleLabel('users', 'total_users')}</div>
        </div>
        
        <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-800 dark:text-green-200">
            {users.filter(u => u.status === 'active').length}
          </div>
          <div className="text-green-600 dark:text-green-400 text-sm">🟢 {getModuleLabel('users', 'active_users')}</div>
        </div>
        
        <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
            {users.filter(u => u.role === 'admin').length}
          </div>
          <div className="text-purple-600 dark:text-purple-400 text-sm">🔑 {getModuleLabel('users', 'admin_users')}</div>
        </div>
        
        <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
            {users.filter(u => u.verified).length}
          </div>
          <div className="text-yellow-600 dark:text-yellow-400 text-sm">✅ {getModuleLabel('users', 'verified_users')}</div>
        </div>
        
        <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-red-800 dark:text-red-200">
            {users.filter(u => u.status === 'cancelled').length}
          </div>
          <div className="text-red-600 dark:text-red-400 text-sm">🔴 {getModuleLabel('users', 'cancelled_users')}</div>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">{getModuleLabel('users', 'user_id')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">{getModuleLabel('users', 'name')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">{getModuleLabel('users', 'email')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">{getModuleLabel('users', 'phone')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{getModuleLabel('users', 'role')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{getModuleLabel('users', 'status')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{getModuleLabel('users', 'verified')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{getModuleLabel('users', 'management')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center p-8 text-gray-500 dark:text-gray-400">
                  {getModuleLabel('users', 'no_users_found')}
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="border border-gray-300 dark:border-gray-600 p-3">{user.id}</td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 font-medium">
                    {user.name}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    {user.email}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    {user.phone || '-'}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    }`}>
                      {user.role === 'admin' ? '🔑 Admin' : '👤 User'}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      userStatuses.find(s => s.value === user.status)?.color || 'bg-gray-100 text-gray-800'
                    }`}>
                      {userStatuses.find(s => s.value === user.status)?.label || user.status}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <button
                      onClick={() => toggleVerification(user.id, user.verified, user.name)}
                      className={`text-xl ${user.verified ? 'text-green-500' : 'text-red-500'} hover:scale-110 transition-transform`}
                      title={user.verified ? getModuleLabel('users', 'unverify_user_tooltip') : getModuleLabel('users', 'verify_user_tooltip')}
                    >
                      {user.verified ? '✅' : '❌'}
                    </button>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    <div className="flex justify-center space-x-1">
                      <button
                        onClick={() => handleEdit(user)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs transition-colors duration-200"
                        title={getModuleLabel('users', 'edit_user_tooltip')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.status, user.name)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs transition-colors duration-200"
                        title={getModuleLabel('users', 'change_status_tooltip')}
                      >
                        🔄
                      </button>
                      <button
                        onClick={() => deleteUser(user.id, user.name)}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs transition-colors duration-200"
                        title={getModuleLabel('users', 'delete_user_tooltip')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}